import { type FC, useMemo } from "react";
import type { ColumnsType } from "antd/es/table";
import { ChevronsUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CustomIcon } from "src/components/reusable/CustomIcon";
import StyledTooltip from "src/components/reusable/StyledTooltip/StyledTooltip";
import { getNumberLocale } from "src/utils/numberFormat";
import { readFormattedText } from "src/layouts/Engagements/Overview/Mappers/OverviewMappers";
import type { CustomList } from "../../EngagementInterfaces";
import {
  AUDIENCE_KIND_LABEL_KEYS,
  formatCreatedDate,
  getAudienceListKind,
  isCreatedByDatatram,
  isPredictedList,
  parseCreatedAt,
} from "./audienceListUtils";
import AskDatatramBadge from "./AskDatatramBadge";

const VISIBLE_TAG_COUNT = 2;

const CHANNEL_LABEL_KEYS: Record<string, string> = {
  whatsapp: "audienceOverview.channelWhatsapp",
  email: "audienceOverview.channelEmail",
};

interface AudienceTagsProps {
  tags: string[];
  translateLabel: (_value: string) => string;
}

export const AudienceTags: FC<AudienceTagsProps> = ({ tags, translateLabel }) => {
  const visibleTags = tags.slice(0, VISIBLE_TAG_COUNT);
  const hiddenTags = tags.slice(VISIBLE_TAG_COUNT).map(translateLabel);

  return (
    <div className="audience-overview__tags">
      {visibleTags.map(tag => (
        <span key={tag} className="audience-overview__tag" dir="auto">
          {translateLabel(tag)}
        </span>
      ))}
      {hiddenTags.length > 0 && (
        <StyledTooltip content={hiddenTags.join(", ")}>
          <span className="audience-overview__tag-more">+{hiddenTags.length}</span>
        </StyledTooltip>
      )}
    </div>
  );
};

interface UseAudienceColumnsParams {
  showArchived: boolean;
  selectedIds: number[];
  allSelected: boolean;
  onToggleAll: (_checked: boolean) => void;
  onToggleRow: (_listId: number, _checked: boolean) => void;
  onToggleSort: () => void;
  onOpen: (_list: CustomList) => void;
  onExport: (_list: CustomList) => void;
  onDelete: (_list: CustomList) => void;
  onArchiveToggle: (_list: CustomList) => void;
  translateLabel: (_value: string | null | undefined) => string;
}

export const useAudienceColumns = ({
  showArchived,
  selectedIds,
  allSelected,
  onToggleAll,
  onToggleRow,
  onToggleSort,
  onOpen,
  onExport,
  onDelete,
  onArchiveToggle,
  translateLabel,
}: UseAudienceColumnsParams): ColumnsType<CustomList> => {
  const { t, i18n } = useTranslation();

  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(getNumberLocale(i18n.language), {
        hour: "numeric",
        minute: "2-digit",
      }),
    [i18n.language],
  );

  return useMemo<ColumnsType<CustomList>>(
    () => [
      {
        title: (
          <input
            type="checkbox"
            checked={allSelected}
            onChange={event => onToggleAll(event.target.checked)}
          />
        ),
        key: "select",
        width: 44,
        render: (_, record) => (
          <input
            type="checkbox"
            checked={selectedIds.includes(record.custom_list_id)}
            onChange={event => onToggleRow(record.custom_list_id, event.target.checked)}
          />
        ),
      },
      {
        title: t("audienceOverview.audienceColumn"),
        key: "audience",
        render: (_, record) => (
          <div className="audience-overview__name-cell">
            <div className="audience-overview__name-row">
              <button
                type="button"
                className="audience-overview__name"
                dir="auto"
                onClick={() => onOpen(record)}
              >
                {translateLabel(record.list_name) || record.list_name}
              </button>
              {isCreatedByDatatram(record) && <AskDatatramBadge />}
            </div>
            <p className="audience-overview__description" dir="auto">
              {record.description?.trim() || t("audienceOverview.noDescription")}
            </p>
          </div>
        ),
      },
      {
        title: t("audienceOverview.type"),
        key: "type",
        render: (_, record) => {
          const kind = getAudienceListKind(record.list_type);
          return (
            <span className={`audience-type-badge audience-type-badge--${kind}`}>
              {t(AUDIENCE_KIND_LABEL_KEYS[kind])}
            </span>
          );
        },
      },
      {
        title: t("audienceOverview.emailMembers"),
        key: "totalEmails",
        render: (_, record) => readFormattedText(record.total_emailsFormattedValue) ?? "-",
      },
      {
        title: t("audienceOverview.whatsappMembers"),
        key: "totalPhoneNumbers",
        render: (_, record) => readFormattedText(record.total_numbersFormattedValue) ?? "-",
      },
      {
        title: t("audienceOverview.tags"),
        key: "tags",
        render: (_, record) => {
          const channelKey = CHANNEL_LABEL_KEYS[record.channel?.toLowerCase() ?? ""];
          return (
            <AudienceTags
              tags={[...(channelKey ? [t(channelKey)] : []), ...(record.tags ?? [])]}
              translateLabel={tag => translateLabel(tag) || tag}
            />
          );
        },
      },
      {
        title: (
          <button
            type="button"
            className="audience-overview__sort"
            onClick={onToggleSort}
            aria-label={t("audienceOverview.sortByCreated")}
          >
            {t("audienceOverview.created")}
            <ChevronsUpDown size={12} aria-hidden />
          </button>
        ),
        key: "created",
        render: (_, record) => {
          const createdAt = parseCreatedAt(record.created_at);
          if (!createdAt) {
            return "-";
          }
          return (
            <div className="audience-overview__created">
              <span className="audience-overview__created-date">
                {formatCreatedDate(createdAt, i18n.language)}
              </span>
              <span className="audience-overview__created-time">
                {timeFormatter.format(createdAt.toDate())}
              </span>
            </div>
          );
        },
      },
      {
        title: t("audienceOverview.actions"),
        key: "actions",
        render: (_, record) => {
          const predicted = isPredictedList(record);
          return (
            <div className="table-row-actions audience-overview__actions">
              <CustomIcon
                name={predicted ? "view" : "edit"}
                tooltip={predicted ? t("audienceOverview.view") : t("audienceOverview.edit")}
                onClick={() => onOpen(record)}
              />
              <CustomIcon
                name="download"
                tooltip={t("audienceOverview.exportData")}
                onClick={() => onExport(record)}
              />
              <CustomIcon
                name="delete"
                tooltip={
                  predicted
                    ? t("audienceOverview.predictedDeleteDisabled")
                    : t("audienceOverview.delete")
                }
                className={`audience-actionbuttons ${predicted ? "is-disabled" : ""}`.trim()}
                onClick={predicted ? undefined : () => onDelete(record)}
              />
              <CustomIcon
                name={showArchived ? "unarchive" : "archive"}
                tooltip={
                  showArchived ? t("audienceOverview.unarchive") : t("audienceOverview.archive")
                }
                onClick={() => onArchiveToggle(record)}
              />
            </div>
          );
        },
      },
    ],
    [
      t,
      i18n.language,
      allSelected,
      selectedIds,
      showArchived,
      timeFormatter,
      onToggleAll,
      onToggleRow,
      onToggleSort,
      onOpen,
      onExport,
      onDelete,
      onArchiveToggle,
      translateLabel,
    ],
  );
};
